import React, { useState, useEffect, useCallback } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import BackButton from "../shared/BackButton/BackButton";
import styles from "./Course.module.css";
import {
  fetchCourseOrderData,
  fetchSections,
  addSection,
  updateCourseSectionLink,
  updateCourseSectionLinksBatch,
  CourseMetadataItem,
} from "../API/Course/CourseAPI";
import { SectionData } from "../API/types";

// Utility: numeric sort for string identifiers like "1", "2", "10"
function numericIdentifierSort(a: string, b: string) {
  const aNum = parseInt(a, 10);
  const bNum = parseInt(b, 10);

  // If both parse to numbers successfully, sort numerically
  if (!isNaN(aNum) && !isNaN(bNum)) {
    return aNum - bNum;
  }
  // Otherwise fall back to standard string comparison
  return a.localeCompare(b);
}

const AVAILABLE_LANGUAGES = [
  "English",
  "Spanish",
  "Italian",
  "French",
  "German",
  "Portuguese",
];
const langCodeMap: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

const Course: React.FC = () => {
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [courseMetadata, setCourseMetadata] = useState<CourseMetadataItem[]>(
    []
  );
  const [selectedIdentifier, setSelectedIdentifier] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [courseLoading, setCourseLoading] = useState(false);

  const [orderedSectionIdentifiers, setOrderedSectionIdentifiers] = useState<
    string[]
  >([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [newSectionIdentifierAdd, setNewSectionIdentifierAdd] = useState("");
  const [sectionFilterText, setSectionFilterText] = useState("");

  const [showEditCoursesModal, setShowEditCoursesModal] = useState(false);
  const [editedCoursesMetadata, setEditedCoursesMetadata] = useState<
    CourseMetadataItem[]
  >([]);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // --- Load course order data ---
  useEffect(() => {
    async function loadCourseOrder() {
      if (!sourceLanguage) {
        setCourseMetadata([]);
        setSelectedIdentifier("");
        setError(null);
        return;
      }
      setCourseLoading(true);
      setError(null);
      try {
        const targetLangCode = langCodeMap[sourceLanguage];
        const data = await fetchCourseOrderData(targetLangCode);
        if (!data || data.length === 0) {
          setCourseMetadata([]);
          setSelectedIdentifier("");
        } else {
          // Sort by numeric ID when possible
          const sortedData = [...data].sort((a, b) =>
            numericIdentifierSort(a.Identifier, b.Identifier)
          );
          setCourseMetadata(sortedData);
          setSelectedIdentifier(sortedData[0]?.Identifier || "");
        }
      } catch (err: any) {
        console.error("Error fetching course order data:", err);
        setError("Failed to load course order data. Please try again.");
        setCourseMetadata([]);
        setSelectedIdentifier("");
      } finally {
        setCourseLoading(false);
      }
    }
    loadCourseOrder();
  }, [sourceLanguage]);

  // --- Load section identifiers ---
  const loadSectionData = useCallback(
    async (maintainOrder = false, currentOrder?: string[]) => {
      if (!sourceLanguage) {
        setOrderedSectionIdentifiers([]);
        setSectionsError(null);
        return;
      }
      setSectionsLoading(true);
      setSectionsError(null);
      try {
        const targetLangCode = langCodeMap[sourceLanguage];
        console.log("Fetching sections for:", targetLangCode);
        const identifiers = await fetchSections(targetLangCode);
        console.log("Fetched sections:", identifiers);
        // Sort numerically if possible
        const sortedIdentifiers = [...identifiers].sort((a, b) =>
          numericIdentifierSort(a, b)
        );

        if (maintainOrder && currentOrder) {
          const fetchedSet = new Set(sortedIdentifiers);
          const currentSet = new Set(currentOrder);
          const newOrder = currentOrder.filter((id) => fetchedSet.has(id));
          sortedIdentifiers.forEach((id) => {
            if (!currentSet.has(id)) newOrder.push(id);
          });
          setOrderedSectionIdentifiers(newOrder);
        } else {
          setOrderedSectionIdentifiers(sortedIdentifiers);
        }
      } catch (err: any) {
        console.error("Error fetching section identifiers:", err);
        setSectionsError("Failed to load section list.");
        setOrderedSectionIdentifiers([]);
      } finally {
        setSectionsLoading(false);
      }
    },
    [sourceLanguage]
  );

  useEffect(() => {
    loadSectionData();
  }, [loadSectionData]);

  // --- Handlers ---
  const handleAddSection = async () => {
    const identifierToAdd = newSectionIdentifierAdd.trim(); // e.g., "Greetings_1"
    if (!identifierToAdd) {
      alert("Please enter a section identifier.");
      return;
    }
    // Check for existence using the user-typed identifier before composing
    if (
      orderedSectionIdentifiers.some((id) =>
        id.startsWith(identifierToAdd + "_")
      )
    ) {
      // A more robust check might be needed if non-composite IDs can exist
      // This checks if any existing ID starts with what the user typed + underscore
      // Or simply check against the exact composite ID if you expect them all to follow the pattern:
      // const potentialCompositeId = `${identifierToAdd}_${langCodeMap[sourceLanguage]}_general`;
      // if (orderedSectionIdentifiers.includes(potentialCompositeId)) { ... }
      // For now, we'll stick to checking based on user input, assuming the composite ID will be unique overall.
      // A simple check for exact match against *existing fetched IDs* might be sufficient if those IDs are already composite.
      // Let's refine the check - assume existing IDs might NOT be composite yet, so we check the typed prefix
      // And we will prevent adding if the *exact composite ID* already exists if fetched.

      // Fetch language code needed for composite check
      const targetLangCode = langCodeMap[sourceLanguage];
      if (!targetLangCode) {
        alert("Please select a language first.");
        return;
      }
      const domain = "general"; // Define the domain part
      const compositeIdentifier = `${identifierToAdd}_${targetLangCode}_${domain}`;

      if (orderedSectionIdentifiers.includes(compositeIdentifier)) {
        alert(
          `Section identifier "${compositeIdentifier}" seems to already exist.`
        );
        return;
      }
      // If the user-typed value exactly matches an existing ID, also prevent
      if (orderedSectionIdentifiers.includes(identifierToAdd)) {
        alert(
          `A section with the exact identifier "${identifierToAdd}" already exists. Please use a different name.`
        );
        return;
      }
    }

    const targetLangCode = langCodeMap[sourceLanguage];
    if (!targetLangCode) {
      alert("Please select a language first.");
      return;
    }

    // --- Construct the Composite Identifier ---
    const domain = "general"; // Or get this from somewhere if dynamic
    const compositeIdentifier = `${identifierToAdd}_${targetLangCode}_${domain}`;
    // --- End Identifier Construction ---

    // Use the user-typed identifier for confirmation message
    const confirmed = window.confirm(
      `This will add a new section record with the effective Identifier "${compositeIdentifier}" based on your input "${identifierToAdd}" for ${sourceLanguage}.\n\nAre you sure?`
    );
    if (!confirmed) return;

    // Use the composite identifier in the payload sent to the API
    const sectionPayload: SectionData = {
      Identifier: compositeIdentifier, // Use the composite ID here
      Targ_Lang_Code: targetLangCode,
      Lessons: [], // Assuming empty lessons initially
    };

    // Indicate processing
    setSectionsLoading(true); // Use sections loading state

    try {
      await addSection(sectionPayload); // This sends the payload with the composite ID
      // Use the user-typed identifier for success message
      alert(
        `Section based on "${identifierToAdd}" (using ID: "${compositeIdentifier}") added successfully!`
      );
      setNewSectionIdentifierAdd(""); // Clear input
      setSectionFilterText(""); // Clear filter
      // Reload section data, maintaining current visual order as much as possible
      await loadSectionData(true, orderedSectionIdentifiers);
    } catch (err) {
      console.error("Error adding section:", err);
      alert(
        `Failed to add section: ${
          err instanceof Error ? err.message : "Unknown error"
        }`
      );
    } finally {
      setSectionsLoading(false); // Stop indicating processing
    }
  };

  const handleCourseSectionChange = (
    identifier: string,
    newSectionValue: string
  ) => {
    setEditedCoursesMetadata((prev) =>
      prev.map((course) =>
        course.Identifier === identifier
          ? { ...course, Vocabulary_List: newSectionValue }
          : course
      )
    );
  };

  const handleSaveChanges = async () => {
    const targetLangCode = langCodeMap[sourceLanguage];
    if (!targetLangCode) {
      alert("Please select a language first.");
      return;
    }

    // Compare the edited data vs. the original data, only update changed items
    const changedCourses = editedCoursesMetadata.filter((editedCourse) => {
      const originalCourse = courseMetadata.find(
        (c) => c.Identifier === editedCourse.Identifier
      );
      return (
        originalCourse &&
        (originalCourse.Vocabulary_List || "") !==
          (editedCourse.Vocabulary_List || "")
      );
    });

    if (changedCourses.length === 0) {
      alert("No changes detected.");
      setShowEditCoursesModal(false);
      return;
    }

    setIsSavingChanges(true);
    const updatePromises: Promise<any>[] = [];

    changedCourses.forEach((course) => {
      updatePromises.push(
        updateCourseSectionLink(
          targetLangCode,
          course.Identifier,
          course.Vocabulary_List || ""
        )
      );
    });

    try {
      await Promise.all(updatePromises);
      alert(`Successfully updated ${changedCourses.length} course(s).`);
      setCourseMetadata((prev) =>
        prev.map((orig) => {
          const edited = editedCoursesMetadata.find(
            (ed) => ed.Identifier === orig.Identifier
          );
          return edited ? { ...edited } : orig;
        })
      );
      setShowEditCoursesModal(false);
    } catch (err) {
      console.error("Error saving course changes:", err);
      alert(
        `Failed to save changes: ${
          err instanceof Error ? err.message : "Unknown error"
        }`
      );
    } finally {
      setIsSavingChanges(false);
    }
  };

  // Handle drag end for reordering courses
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    const newOrder = [...editedCoursesMetadata];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destIndex, 0, removed);

    setEditedCoursesMetadata(newOrder);
    setOrderChanged(true);
  };

  // Save the new course order
  // The Identifiers (1, 2, 3...) represent positions, so to reorder we need to
  // reassign which Vocabulary_List goes to which position/Identifier
  const handleSaveOrder = async () => {
    const targetLangCode = langCodeMap[sourceLanguage];
    if (!targetLangCode) {
      alert("Please select a language first.");
      return;
    }

    // Get the original courses sorted by their Identifier (position)
    const originalSorted = [...courseMetadata].sort((a, b) =>
      numericIdentifierSort(a.Identifier, b.Identifier)
    );

    // The editedCoursesMetadata is in the new visual order
    // We need to assign the Vocabulary_List values to the positions
    const updates: { Identifier: string; Vocabulary_List: string }[] = [];

    editedCoursesMetadata.forEach((course, newIndex) => {
      const targetIdentifier = originalSorted[newIndex]?.Identifier;
      if (targetIdentifier && course.Vocabulary_List) {
        // Check if this is actually a change
        const originalAtPosition = originalSorted[newIndex];
        if (originalAtPosition?.Vocabulary_List !== course.Vocabulary_List) {
          updates.push({
            Identifier: targetIdentifier,
            Vocabulary_List: course.Vocabulary_List,
          });
        }
      }
    });

    if (updates.length === 0) {
      alert("No changes to save.");
      setOrderChanged(false);
      return;
    }

    setIsSavingOrder(true);
    try {
      // Update all changed positions in a single API call
      await updateCourseSectionLinksBatch(targetLangCode, updates);
      alert(`Course order saved! Updated ${updates.length} position(s).`);

      // Rebuild the metadata with new assignments
      const newMetadata = originalSorted.map((course, index) => ({
        ...course,
        Vocabulary_List: editedCoursesMetadata[index]?.Vocabulary_List || course.Vocabulary_List,
      }));
      setCourseMetadata(newMetadata);
      setEditedCoursesMetadata(newMetadata);
      setOrderChanged(false);
    } catch (err) {
      console.error("Error saving course order:", err);
      alert(
        `Failed to save order: ${
          err instanceof Error ? err.message : "Unknown error"
        }`
      );
    } finally {
      setIsSavingOrder(false);
    }
  };

  // Provide a numeric sort in the dropdown
  const sortedCourseMetadata = [...courseMetadata].sort((a, b) =>
    numericIdentifierSort(a.Identifier, b.Identifier)
  );

  // Extract unique Vocabulary_List values from course metadata for the dropdown
  const availableSections = courseMetadata
    .map((c) => c.Vocabulary_List)
    .filter((v): v is string => !!v) // filter out empty/null values
    .filter((value, index, self) => self.indexOf(value) === index) // unique
    .sort((a, b) => a.localeCompare(b)); // alphabetical sort

  // Filter sections in manage sections modal
  const filteredSections = orderedSectionIdentifiers.filter((id) =>
    id.toLowerCase().includes(sectionFilterText.toLowerCase())
  );

  const selectedCourseMeta = courseMetadata.find(
    (course) => course.Identifier === selectedIdentifier
  );

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Course Creator</h1>

      {/* Language and Course Selection Dropdowns */}
      <div className={styles.formRow}>
        <label className={styles.label}>
          <strong>Choose Language:</strong>
          <select
            value={sourceLanguage}
            onChange={(e) => {
              setSourceLanguage(e.target.value);
              setSelectedIdentifier("");
              setShowEditCoursesModal(false);
              setIsManageModalOpen(false);
            }}
            className={styles.input}
          >
            <option value="">Select a language</option>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </label>
        {sourceLanguage && !courseLoading && courseMetadata.length > 0 && (
          <label className={styles.label}>
            <strong>Choose Course:</strong>
            <select
              value={selectedIdentifier}
              onChange={(e) => setSelectedIdentifier(e.target.value)}
              className={styles.input}
            >
              {sortedCourseMetadata.map((course) => (
                <option key={course.Identifier} value={course.Identifier}>
                  {`${course.Identifier} - Section: ${
                    course.Vocabulary_List || "N/A"
                  }`}
                </option>
              ))}
            </select>
          </label>
        )}
        {sourceLanguage && courseLoading && <p>Loading course order...</p>}
        {error && (
          <div className={`${styles.error} ${styles.fullWidth}`}>{error}</div>
        )}
        {sourceLanguage &&
          !courseLoading &&
          courseMetadata.length === 0 &&
          !error && <p>No courses found for this language.</p>}
      </div>

      {/* Display Selected Course Details (Simplified View) */}
      {selectedCourseMeta && !showEditCoursesModal && (
        <>
          <h2>Course Details</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Identifier</th>
                <th>Associated Section</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{selectedCourseMeta.Identifier}</td>
                <td>{selectedCourseMeta.Vocabulary_List || "Not Set"}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* Action Buttons Container */}
      {!showEditCoursesModal && sourceLanguage && (
        <div className={styles.actionButtonsContainer}>
          <button
            onClick={() => setIsManageModalOpen(true)}
            className={styles.actionButton}
            disabled={sectionsLoading || !!sectionsError}
          >
            Manage Sections
          </button>
          <button
            onClick={() => {
              // Deep copy to allow edits
              setEditedCoursesMetadata(
                JSON.parse(JSON.stringify(courseMetadata))
              );
              setOrderChanged(false);
              setShowEditCoursesModal(true);
            }}
            className={styles.actionButton}
            disabled={courseLoading || courseMetadata.length === 0}
          >
            Edit Courses
          </button>
          {sectionsLoading && <p>Loading sections...</p>}
          {sectionsError && !sectionsLoading && (
            <div className={`${styles.error} ${styles.inlineError}`}>
              {sectionsError}
            </div>
          )}
        </div>
      )}

      {/* Edit Courses Modal */}
      {showEditCoursesModal && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setShowEditCoursesModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px" }}
          >
            <h2>Edit Courses for {sourceLanguage}</h2>
            <p>Drag courses to reorder. Change the associated section for each course.</p>
            {courseLoading && <p>Loading courses...</p>}
            {error && <div className={styles.error}>{error}</div>}
            {!courseLoading && !error && (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="courses-list">
                  {(provided) => (
                    <div
                      className={styles.scrollableDndList}
                      style={{ maxHeight: "500px", overflowY: "auto" }}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {editedCoursesMetadata.map((course, index) => (
                        <Draggable
                          key={course.Identifier}
                          draggableId={course.Identifier}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`${styles.dndItem} ${
                                snapshot.isDragging ? styles.draggingItem : ""
                              }`}
                              style={{
                                ...provided.draggableProps.style,
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <span className={styles.dragHandle}>☰</span>
                              <span className={styles.courseNumber}>
                                {index + 1}.
                              </span>
                              <span className={styles.courseIdentifier}>
                                Course {course.Identifier}
                              </span>
                              <span style={{ fontSize: "11px", color: "#666", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {course.Vocabulary_List || "(none)"}
                              </span>
                              <select
                                value={course.Vocabulary_List || ""}
                                onChange={(e) =>
                                  handleCourseSectionChange(
                                    course.Identifier,
                                    e.target.value
                                  )
                                }
                                className={styles.input}
                                style={{ flex: 1, margin: 0 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="">-- Select Section --</option>
                                {availableSections.map((secId) => (
                                  <option key={secId} value={secId}>
                                    {secId}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <div className={styles.modalActions}>
              {orderChanged && (
                <button
                  type="button"
                  onClick={handleSaveOrder}
                  className={styles.actionButton}
                  disabled={isSavingOrder || isSavingChanges}
                >
                  {isSavingOrder ? "Saving Order..." : "Save Order"}
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveChanges}
                className={styles.saveButton}
                disabled={
                  courseLoading ||
                  !!error ||
                  isSavingChanges ||
                  isSavingOrder
                }
              >
                {isSavingChanges ? "Saving..." : "Save Section Changes"}
              </button>
              <button
                type="button"
                onClick={() => setShowEditCoursesModal(false)}
                className={styles.clearButton}
                disabled={isSavingChanges || isSavingOrder}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Sections Modal */}
      {isManageModalOpen && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setIsManageModalOpen(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Manage Sections for {sourceLanguage}</h2>
            <p>View existing sections or add a new one.</p>

            <label className={styles.label}>
              Add/Filter Section Identifier:
              <div className={styles.inputWithButton}>
                <input
                  type="text"
                  value={newSectionIdentifierAdd}
                  onChange={(e) => {
                    setNewSectionIdentifierAdd(e.target.value);
                    setSectionFilterText(e.target.value);
                  }}
                  placeholder="Type to filter or add new section ID"
                  className={styles.input}
                  disabled={sectionsLoading || !sourceLanguage}
                />
                <button
                  type="button"
                  onClick={handleAddSection}
                  className={styles.inlineButton}
                  disabled={
                    !newSectionIdentifierAdd.trim() ||
                    sectionsLoading ||
                    !sourceLanguage ||
                    orderedSectionIdentifiers.includes(
                      newSectionIdentifierAdd.trim()
                    )
                  }
                  title={
                    orderedSectionIdentifiers.includes(
                      newSectionIdentifierAdd.trim()
                    )
                      ? "Section already exists"
                      : "Add new section"
                  }
                >
                  Add Section
                </button>
              </div>
            </label>
            <hr />
            <strong>Existing Sections:</strong>
            {sectionsLoading && <p>Loading...</p>}
            {sectionsError && (
              <div className={styles.error}>{sectionsError}</div>
            )}
            {!sectionsLoading && !sectionsError && (
              <div
                className={`${styles.scrollableDndList} ${styles.sectionList}`}
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                {filteredSections.length > 0 ? (
                  <ul>
                    {filteredSections.map((id) => (
                      <li key={id}>{id}</li>
                    ))}
                  </ul>
                ) : (
                  <p>
                    {sectionFilterText
                      ? "No matching sections found."
                      : "No sections exist yet."}
                  </p>
                )}
              </div>
            )}
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setIsManageModalOpen(false)}
                className={styles.clearButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Course;
